/**
 * Addresses Page
 *
 * Manages the user's saved shipping addresses stored in the
 * customer_addresses table in Supabase.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Divider,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeIcon from '@mui/icons-material/Home';

const Addresses = () => {
  const { customer } = useAuth();

  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [addressState, setAddressState] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('US');
  const [isDefault, setIsDefault] = useState(false);

  const fetchAddresses = useCallback(async () => {
    if (!customer?.id) return;
    setLoadingAddresses(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('customer_addresses')
        .select('*')
        .eq('customer_id', customer.id)
        .order('is_default', { ascending: false });
      if (fetchError) throw fetchError;
      setAddresses(data ?? []);
    } catch (err) {
      setError(err.message || 'Failed to load addresses');
    } finally {
      setLoadingAddresses(false);
    }
  }, [customer?.id]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const resetForm = () => {
    setName('');
    setAddress1('');
    setAddress2('');
    setCity('');
    setAddressState('');
    setZip('');
    setCountry('US');
    setIsDefault(false);
    setCurrentAddress(null);
  };

  const handleOpenAddDialog = () => {
    resetForm();
    if (addresses.length === 0) setIsDefault(true);
    setOpenAddDialog(true);
  };

  const handleOpenEditDialog = (address) => {
    setCurrentAddress(address);
    setName(address.name || '');
    setAddress1(address.address1 || '');
    setAddress2(address.address2 || '');
    setCity(address.city || '');
    setAddressState(address.state || '');
    setZip(address.zip || '');
    setCountry(address.country || 'US');
    setIsDefault(address.is_default || false);
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (address) => {
    setCurrentAddress(address);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    resetForm();
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // If setting as default, clear existing default first
      if (isDefault) {
        await supabase
          .from('customer_addresses')
          .update({ is_default: false })
          .eq('customer_id', customer.id);
      }

      const { error: insertError } = await supabase
        .from('customer_addresses')
        .insert({
          customer_id: customer.id,
          name,
          address1,
          address2: address2 || null,
          city,
          state: addressState,
          zip,
          country,
          is_default: isDefault,
        });

      if (insertError) throw insertError;
      setSuccess('Address added successfully');
      handleCloseDialogs();
      await fetchAddresses();
    } catch (err) {
      setError(err.message || 'Failed to add address');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAddress = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // If setting as default, clear existing default first
      if (isDefault) {
        await supabase
          .from('customer_addresses')
          .update({ is_default: false })
          .eq('customer_id', customer.id);
      }

      const { error: updateError } = await supabase
        .from('customer_addresses')
        .update({
          name,
          address1,
          address2: address2 || null,
          city,
          state: addressState,
          zip,
          country,
          is_default: isDefault,
          updated_at: new Date().toISOString(),
        })
        .eq('id', currentAddress.id)
        .eq('customer_id', customer.id);

      if (updateError) throw updateError;
      setSuccess('Address updated successfully');
      handleCloseDialogs();
      await fetchAddresses();
    } catch (err) {
      setError(err.message || 'Failed to update address');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { error: deleteError } = await supabase
        .from('customer_addresses')
        .delete()
        .eq('id', currentAddress.id)
        .eq('customer_id', customer.id);

      if (deleteError) throw deleteError;
      setSuccess('Address deleted successfully');
      handleCloseDialogs();
      await fetchAddresses();
    } catch (err) {
      setError(err.message || 'Failed to delete address');
    } finally {
      setLoading(false);
    }
  };

  const AddressForm = ({ onSubmit, dialogTitle, submitButtonText }) => (
    <Box component="form" onSubmit={onSubmit} noValidate>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <TextField
          margin="normal" required fullWidth id="name" label="Full Name" name="name"
          value={name} onChange={(e) => setName(e.target.value)}
        />
        <TextField
          margin="normal" required fullWidth id="address1" label="Address Line 1" name="address1"
          value={address1} onChange={(e) => setAddress1(e.target.value)}
        />
        <TextField
          margin="normal" fullWidth id="address2" label="Address Line 2 (Optional)" name="address2"
          value={address2} onChange={(e) => setAddress2(e.target.value)}
        />
        <TextField
          margin="normal" required fullWidth id="city" label="City" name="city"
          value={city} onChange={(e) => setCity(e.target.value)}
        />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              margin="normal" required fullWidth id="state" label="State/Province" name="state"
              value={addressState} onChange={(e) => setAddressState(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              margin="normal" required fullWidth id="zip" label="ZIP/Postal Code" name="zip"
              value={zip} onChange={(e) => setZip(e.target.value)}
            />
          </Grid>
        </Grid>
        <TextField
          margin="normal" required fullWidth id="country" label="Country" name="country"
          value={country} onChange={(e) => setCountry(e.target.value)}
        />
        <FormControlLabel
          control={
            <Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} name="isDefault" color="primary" />
          }
          label="Set as default shipping address"
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs} sx={{ color: '#8B4513' }}>Cancel</Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6d3610' } }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : submitButtonText}
        </Button>
      </DialogActions>
    </Box>
  );

  AddressForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    dialogTitle: PropTypes.string.isRequired,
    submitButtonText: PropTypes.string.isRequired,
  };

  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/account" style={{ textDecoration: 'none', color: '#8B4513' }}>My Account</Link>
        <Typography color="text.primary">Addresses</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button component={Link} to="/account" startIcon={<ArrowBackIcon />} sx={{ mr: 2, color: '#8B4513' }}>
          Back
        </Button>
        <Typography variant="h4" component="h1" sx={{ color: '#8B4513' }}>
          Manage Addresses
        </Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Your Addresses</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6d3610' } }}
          >
            Add New Address
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {loadingAddresses ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress sx={{ color: '#8B4513' }} />
          </Box>
        ) : addresses.length > 0 ? (
          <Grid container spacing={3}>
            {addresses.map((address) => (
              <Grid item xs={12} sm={6} key={address.id}>
                <Card variant="outlined">
                  <CardContent>
                    {address.is_default && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: '#8B4513' }}>
                        <HomeIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Typography variant="subtitle2">Default Address</Typography>
                      </Box>
                    )}
                    <Typography variant="subtitle1">{address.name}</Typography>
                    <Typography variant="body2">{address.address1}</Typography>
                    {address.address2 && <Typography variant="body2">{address.address2}</Typography>}
                    <Typography variant="body2">
                      {address.city}, {address.state} {address.zip}
                    </Typography>
                    <Typography variant="body2">{address.country}</Typography>
                  </CardContent>
                  <CardActions>
                    <IconButton aria-label="edit" onClick={() => handleOpenEditDialog(address)} sx={{ color: '#8B4513' }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleOpenDeleteDialog(address)}
                      sx={{ color: '#8B4513' }}
                      disabled={addresses.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" paragraph>
              You don&apos;t have any saved addresses yet.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6d3610' } }}
            >
              Add New Address
            </Button>
          </Box>
        )}
      </Paper>

      <Dialog open={openAddDialog} onClose={handleCloseDialogs} fullWidth maxWidth="sm">
        <AddressForm onSubmit={handleAddAddress} dialogTitle="Add New Address" submitButtonText="Add Address" />
      </Dialog>

      <Dialog open={openEditDialog} onClose={handleCloseDialogs} fullWidth maxWidth="sm">
        <AddressForm onSubmit={handleUpdateAddress} dialogTitle="Edit Address" submitButtonText="Update Address" />
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={handleCloseDialogs}>
        <DialogTitle>Delete Address</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this address?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} sx={{ color: '#8B4513' }}>Cancel</Button>
          <Button onClick={handleDeleteAddress} variant="contained" color="error" disabled={loading}>
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Addresses;
